import React, { useMemo } from 'react';
import SymbologySwatch from '../LayerProperties/Symbology/components/SymbologySwatch';
import { buildSingleConfig, resolveDisplayLabel } from '../LayerProperties/Symbology/symbologyConfig.utils';
import { SingleSymbologyConfig, SymbologyConfig } from '../../../types/symbology.types';
import './LegendDrawer.scss';

interface LegendEntry {
  symbology: SingleSymbologyConfig;
  label: string;
}

/**
 * Renders a layer's applied symbology as legend entries (icon/swatch + label),
 * expanded beneath the layer row. Reads the typed `symbologyConfig`:
 *   • single   → one entry (the layer's symbol + title)
 *   • unique   → one entry per category (icon + display label)
 *   • quantity → one entry per class break (swatch + display/range)
 *   • heatmap  → the colour ramp gradient
 * Falls back to the flat single fields for legacy layers without a config.
 * `descriptionOnly` renders just the layer description (raster/WMS layers).
 */
const LegendLayerSymbology: React.FC<{ layer: Record<string, any>; descriptionOnly?: boolean }> = ({ layer, descriptionOnly = false }) => {
  const layerType: string = layer.layerType || '';
  const isPointLayer = layerType === 'Point' || layerType === 'MultiPoint';
  const isLineLayer = layerType.includes('Line');
  const config: SymbologyConfig | undefined = layer.symbologyConfig ?? undefined;
  const description: string | undefined = layer.layerDescription;

  const entries: LegendEntry[] = useMemo(() => {
    if (config?.type === 'unique') {
      return config.categories.map((c) => ({ symbology: c.symbology, label: resolveDisplayLabel(c) }));
    }
    if (config?.type === 'quantity') {
      return config.classes.map((c) => ({ symbology: c.symbology, label: c.displayLabel?.trim() || c.expression || '' }));
    }
    // single (typed) or legacy flat fields
    const single = config?.type === 'single' ? config : buildSingleConfig(layer as any);
    return [{ symbology: single, label: layer.layerTitle || layer.uniqueLayerName }];
  }, [config, layer]);

  // Raster/WMS layers have no vector symbology but still show their description.
  if (descriptionOnly) {
    return description ? (
      <div className="legend-symbology">
        <div className="legend-symbology__desc">{description}</div>
      </div>
    ) : null;
  }

  return (
    <div className="legend-symbology">
      {description && <div className="legend-symbology__desc">{description}</div>}

      {config?.type === 'heatmap' ? (
        <div className="legend-symbology__entry">
          <span
            className="legend-symbology__ramp"
            style={{ background: `linear-gradient(to right, ${config.colorRamp.colors.join(', ')})` }}
            aria-label="Heatmap colour ramp"
          />
          <span className="legend-symbology__label">Heatmap</span>
        </div>
      ) : (
        entries.map((e, i) => (
          <div className="legend-symbology__entry" key={i}>
            <span className="legend-symbology__swatch">
              <SymbologySwatch symbology={e.symbology} isPointLayer={isPointLayer} isLineLayer={isLineLayer} />
            </span>
            <span className="legend-symbology__label" title={e.label}>
              {e.label}
            </span>
          </div>
        ))
      )}
    </div>
  );
};

export default LegendLayerSymbology;
